import {
  type DateValue,
  fromDate,
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
  today,
} from "@internationalized/date"
import { prettyPrintDate } from "@prismicio/editor-support/Date"
import { composeRefs } from "@prismicio/editor-support/React"
import {
  type AriaDatePickerProps,
  type TimeValue,
  useDatePicker,
} from "@react-aria/datepicker"
import { useDatePickerState } from "@react-stately/datepicker"
import { forwardRef, useRef, useState } from "react"

import { enLocale } from "../../utils/date"
import { Button } from "../Button"
import { Calendar } from "../Calendar"
import { Card, CardContent } from "../Card"
import { Popover } from "../Popover"
import { TimeField } from "../TimeField"
import { Tooltip } from "../Tooltip"
import styles from "./DatePicker.module.css"

export interface DatePickerProps {
  value: Date | undefined
  minValue?: "today"
  minTime?: "now"
  onSubmit?: (date: Date) => void
  disabled?: boolean
  triggerSize?: "small" | "medium" | "large"
  /** Accessible label for the date picker */
  label?: string
  /** Fallback text to display if the date picker has no value */
  fallback?: string
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  function DatePicker(props, ref) {
    const {
      value,
      minValue,
      minTime,
      onSubmit,
      disabled = false,
      triggerSize = "small",
      label,
      fallback = "No date selected",
      ...implicitProps
    } = props

    const [date, setDate] = useState<DateValue>(
      fromDate(value ?? new Date(), getLocalTimeZone()),
    )
    const triggerRef = useRef<HTMLButtonElement>(null)

    const locale = enLocale()
    const defaultTime = value && parseAbsoluteToLocal(value?.toISOString())

    const datePickerProps: AriaDatePickerProps<DateValue> = {
      "aria-labelledby": "date-picker",
      placeholderValue: undefined,
      value: date,
      onChange: setDate,
    }

    const state = useDatePickerState({
      ...datePickerProps,
      shouldCloseOnSelect: false,
    })

    const { calendarProps } = useDatePicker(datePickerProps, state, triggerRef)

    function toggle() {
      state.setOpen(!state.isOpen)
    }

    function onDateChange(date: DateValue) {
      calendarProps.onChange?.(date)
    }

    function onTimeChange(time: TimeValue) {
      state.setTimeValue(time)
      calendarProps.onChange?.(state.dateValue)
    }

    function onPrimaryAction() {
      onSubmit?.(state.dateValue.toDate(getLocalTimeZone()))
      toggle()
    }

    const isTimeAfterNow = minTime === "now" ? getIsTimeAfterNow(date) : true
    const isDateTodayOrAfter =
      minValue === "today" ? date > today(getLocalTimeZone()) : true
    const isValid = isDateTodayOrAfter && isTimeAfterNow

    function getTooltipMessage() {
      if (!isDateTodayOrAfter) {
        return "The date must be after today"
      }

      if (!isTimeAfterNow) {
        return "The time must be after now"
      }

      return ""
    }

    return (
      <Popover
        open={state.isOpen}
        align="start"
        sideOffset={4}
        anchor={
          <Button
            {...implicitProps}
            ref={composeRefs(triggerRef, ref)}
            size={triggerSize}
            color="grey"
            onClick={toggle}
            disabled={disabled}
            hiddenLabel={label}
          >
            {value ? prettyPrintDate(value, locale) : fallback}
          </Button>
        }
        onInteractOutside={toggle}
        onEscapeKeyDown={toggle}
      >
        <Card radius={4} paddingBlock={16}>
          <CardContent>
            <Calendar
              value={calendarProps.value ?? undefined}
              minValue={minValue}
              onValueChange={onDateChange}
            />

            <div className={styles.actions}>
              <TimeField
                defaultValue={defaultTime}
                onChange={onTimeChange}
                sx={{ flexGrow: 1 }}
              />
              <Tooltip content={getTooltipMessage()} visible={!isValid}>
                <Button onClick={onPrimaryAction} disabled={!isValid}>
                  Apply
                </Button>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </Popover>
    )
  },
)

function getIsTimeAfterNow(date: DateValue) {
  const selectedDate = date.toDate(getLocalTimeZone())
  const selectedDateHours = selectedDate.getHours()
  const selectedDateMinutes = selectedDate.getMinutes()

  const nowDate = now(getLocalTimeZone()).toDate()
  const nowDateHours = nowDate.getHours()
  const nowDateMinutes = nowDate.getMinutes()

  function isSameDate(date: Date) {
    return (
      date.getDate() === nowDate.getDate() &&
      date.getMonth() === nowDate.getMonth() &&
      date.getFullYear() === nowDate.getFullYear()
    )
  }

  if (isSameDate(selectedDate)) {
    return (
      selectedDateHours > nowDateHours ||
      (selectedDateHours === nowDateHours &&
        selectedDateMinutes > nowDateMinutes)
    )
  }

  // if it isn't the same date, then we don't care about locking the time
  return true
}
